import { css } from 'styled-components';
import Color from 'color';

import {
  IssueType,
  IssueStatus,
  IssuePriority,
  IssueStatusForClient,
} from 'shared/constants/issues';
import { getCurrentLanguageFonts } from './fontConfig';

export const color = {
  primary: '#0052cc', // Blue
  secondary: '#F4F5F7', // light grey

  info: '#0052cc', // Blue
  success: '#0B875B', // green
  warning: '#F89C1C', // orange
  danger: '#E13C3C', // red
  error: '#E13C3C', // red

  textDarkest: '#172b4d',
  textDark: '#42526E',
  textMedium: '#5E6C84',
  textLight: '#8993a4',
  textLink: '#0052cc',
  textPrimary: '#ffffff',

  backgroundDarkPrimary: '#0747A6',
  backgroundMedium: '#dfe1e6',
  backgroundLight: '#ebecf0',
  backgroundLightest: '#F4F5F7',
  backgroundLightPrimary: '#D2E5FE',
  backgroundLightSuccess: '#E4FCEF',

  borderLightest: '#dfe1e6',
  borderLight: '#C1C7D0',
  borderInputFocus: '#4c9aff',

  sidebarPrimary: '#061F5F',
  topbarPrimary: '#1E356F',
  addBarPrimaryLight: '#DFE1EF',
};

export const issueTypeColors = {
  [IssueType.TASK]: '#4FADE6', // blue
  // [IssueType.STORY]: '#65BA43', // green
};

export const issuePriorityColors = {
  [IssuePriority.HIGHEST]: '#CD1317', // red
  [IssuePriority.HIGH]: '#E9494A', // orange
  [IssuePriority.MEDIUM]: '#E97F33', // orange
  [IssuePriority.LOW]: '#2D8738', // green
  // [IssuePriority.LOWEST]: '#57A55A', // green
};

export const issueStatusColors = {
  [IssueStatus.NEWTASK]: color.textDark,
  [IssueStatus.CHECKREADY]: color.textPrimary,
  [IssueStatus.DOCTORASSIGNED]: color.textPrimary,
  [IssueStatus.REPORTREADY]: color.textPrimary,
  [IssueStatus.SENDBACK]: color.textPrimary,
  [IssueStatus.DONE]: color.textDark,
  [IssueStatusForClient.NEWTASK]: color.textDark,
  [IssueStatusForClient.CHECKREADY]: color.textPrimary,
  [IssueStatusForClient.DOCTORASSIGNED]: color.textPrimary,
  [IssueStatusForClient.REPORTREADY]: color.textPrimary,
  [IssueStatusForClient.SENDBACK]: color.textPrimary,
  [IssueStatusForClient.DONE]: color.textDark,
};

export const issueStatusBackgroundColors = {
  [IssueStatus.NEWTASK]: color.warning,
  [IssueStatus.CHECKREADY]: color.primary,
  [IssueStatus.DOCTORASSIGNED]: color.primary,
  [IssueStatus.REPORTREADY]: color.primary,
  [IssueStatus.SENDBACK]: color.success,
  [IssueStatus.DONE]: color.backgroundMedium,
  [IssueStatusForClient.NEWTASK]: color.warning,
  [IssueStatusForClient.CHECKREADY]: color.primary,
  [IssueStatusForClient.DOCTORASSIGNED]: color.primary,
  [IssueStatusForClient.REPORTREADY]: color.primary,
  [IssueStatusForClient.SENDBACK]: color.success,
  [IssueStatusForClient.DONE]: color.backgroundMedium,
};

export const sizes = {
  appNavBarLeftWidth: 64,
  secondarySideBarWidth: 230,
  minViewportWidth: 1000,
};

// The zIndex value should be controlled on this location
//! MUI Appbar seems to be of 1100
//! MUI Menu seems to be of 1300
export const zIndexValues = {
  bottom: -1,
  dropdown: 101,
  navLeft: 100,
  popover: 100,
  batchEditModal: 1000,
  sidenav: 900,
  modal: 1100,
  modal2: 1150,
  modal3: 1310,
  modal4: 1320,
  modal5: 1330,
  modalMenu: 1340,
  loader: 1400,
  iconPopover: 3000,
};

export const modalWidths = {
  xxs: '400px',
  xs: '540px',
  sm: '740px',
  md: '940px',
  lg: '1140px',
  xl: '1340px',
  xxl: '1540px',
};

export const modalHeights = {
  xs: '50vh',
  sm: '60vh',
  md: '70vh',
  lg: '80vh',
  xl: '90vh',
};

export const modalBorderRadius = {
  sm: '3px',
  md: '5px',
  lg: '8px',
};

export const font = {
  regular: `font-family: ${
    getCurrentLanguageFonts().primary
  }; font-weight: normal;`,
  medium: `font-family: ${
    getCurrentLanguageFonts().secondary
  }; font-weight: normal;`,
  bold: `font-family: ${getCurrentLanguageFonts().bold}; font-weight: normal;`,
  black: `font-family: ${
    getCurrentLanguageFonts().display
  }; font-weight: normal;`,
  size: (size) => `font-size: ${size}px;`,
};

export const mixin = {
  darken: (colorValue, amount) => Color(colorValue).darken(amount).string(),
  lighten: (colorValue, amount) => Color(colorValue).lighten(amount).string(),
  rgba: (colorValue, opacity) => Color(colorValue).alpha(opacity).string(),
  boxShadowMedium: css`
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.05);
  `,

  boxShadowHeavy: css`
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.5);
  `,

  boxShadowDropdown: css`
    box-shadow: rgba(9, 30, 66, 0.25) 0px 4px 8px -2px,
      rgba(9, 30, 66, 0.31) 0px 0px 1px;
  `,
  truncateText: css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
  clickable: css`
    cursor: pointer;
    user-select: none;
  `,

  hardwareAccelerate: css`
    transform: translateZ(0);
  `,
  cover: css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  `,
  placeholderColor: (colorValue) => css`
    ::-webkit-input-placeholder {
      color: ${colorValue} !important;
      opacity: 1 !important;
    }
    :-moz-placeholder {
      color: ${colorValue} !important;
      opacity: 1 !important;
    }
    ::-moz-placeholder {
      color: ${colorValue} !important;
      opacity: 1 !important;
    }
    :-ms-input-placeholder {
      color: ${colorValue} !important;
      opacity: 1 !important;
    }
  `,
  scrollableY: css`
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `,
  scrollableXY: css`
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `,
  customScrollbar: ({
    width = 4,
    background = color.backgroundMedium,
  } = {}) => css`
    &::-webkit-scrollbar {
      width: ${width}px;
    }
    &::-webkit-scrollbar-track {
      background: none;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 99px;
      background: ${background};
    }
  `,

  simpleCustomScrollbar: css`
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 10px;
      border: 12px solid #f1f1f1;
    }

    scrollbar-width: thin;
    // scrollbar-color: #888 #f1f1f1;
  `,

  backgroundImage: (imageURL) => css`
    background-image: url('${imageURL}');
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: ${color.backgroundLight};
  `,
  link: (colorValue = color.textLink) => css`
    cursor: pointer;
    color: ${colorValue};
    ${font.medium}
    &:hover, &:visited, &:active {
      color: ${colorValue};
    }
    &:hover {
      text-decoration: underline;
    }
  `,
  tag: (
    background = color.backgroundMedium,
    colorValue = color.textDarkest
  ) => css`
    display: inline-flex;
    align-items: center;
    height: 24px;
    padding: 0 8px;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    color: ${colorValue};
    background: ${background};
    ${font.bold}
    ${font.size(12)}
    i {
      margin-left: 4px;
    }
  `,
  tagKanbanFilter: (
    background = color.backgroundMedium,
    colorValue = color.textDarkest
  ) => css`
    max-height: 48px;
    max-width: 600px;
    display: inline-flex;
    align-items: center;
    padding: 2px 8px 2px 8px;
    border-radius: 10px;
    cursor: pointer;
    user-select: none;
    color: ${colorValue};
    background: ${background};
    ${font.bold}
    ${font.size(14)}
    i {
      margin-left: 6px;
    }
  `,
  tagIssueListStatus: (
    background = color.backgroundMedium,
    colorValue = color.textDarkest
  ) => css`
    display: inline-flex;
    justify-content: center;
    text-align: center;
    align-items: center;
    min-width: 140px;
    height: 30px;
    padding: 0 8px;
    border-radius: 4px;
    user-select: none;
    color: ${colorValue};
    background: ${background};
    ${font.bold}
    ${font.size(12)}
    i {
      margin-left: 4px;
    }
  `,
};
